import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import { ASSET_MOVE_CONFLICT_CODE } from '@/api/constants';
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getMoveConflictMessage,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import { FormDateTimePicker } from '@/components/form-date-time-picker';
import { FormTextField } from '@/components/form-text-field';
import { SupportSnackbar } from '@/components/support-snackbar';
import { InfoOutlined as InfoOutlinedIcon } from '@/constants/hardfin-standard-icons';
import { fulfillmentsQueryKey } from '@/fulfillment/query-keys';
import { FormLocationSearch } from '@/location/components/form-location-search';
import { getNewLocationURL } from '@/location/utilities/get-new-location-url';
import {
  DeliverShipmentRequest,
  deliverShipment,
} from '@/shipment/api/deliver-shipment';
import { Shipment } from '@/shipment/api/types';
import {
  UpdateShipmentRequest,
  updateShipment,
} from '@/shipment/api/update-shipment';
import { shipmentDetailQueryKey } from '@/shipment/hooks/use-shipment';
import { shipmentsQueryKey } from '@/shipment/hooks/use-shipments';
import {
  calculateCascadeTimeSeconds,
  getTotalSerializedAssetCount,
} from '@/shipment/utilities/estimate-cascade-time';
import { sparseLocationSchema } from '@/sparse/api/types';
import { dateTimeSchema } from '@/utilities/date-time-schema';
import { formatDuration } from '@/utilities/format-duration';
import { pluralize } from '@/utilities/pluralize';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

function createDeliveryFormSchema(
  shippedAt: DateTime | null,
  receivedAt: DateTime | null,
  storedDeliveredAt: DateTime | null
) {
  return z
    .object({
      deliveryDate: dateTimeSchema.nullable(),
      deliveryLocation: sparseLocationSchema.nullable(),
      deliveryNote: z.string().nullable().optional(),
    })
    .superRefine((data, ctx) => {
      // Run validation on submission so we can still have null as a state in the form.
      // The nulls are legal till the form is submitted then errors should be shown.

      // Check if deliveryDate is set (not null)
      if (!data.deliveryDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Delivery date is required',
          path: ['deliveryDate'],
        });
      }

      // Validate delivery date is not before ship date (can be equal)
      if (data.deliveryDate && shippedAt) {
        if (data.deliveryDate < shippedAt) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Delivery date cannot be before ship date',
            path: ['deliveryDate'],
          });
        }
      }

      // A delivery date the person picks falls before the receipt and never in the future. An unchanged
      // stored date is resent as it is, so it is not judged again.
      const isDeliveryDateChanged =
        !storedDeliveredAt ||
        !data.deliveryDate ||
        !data.deliveryDate.equals(storedDeliveredAt);
      if (
        isDeliveryDateChanged &&
        data.deliveryDate &&
        receivedAt &&
        data.deliveryDate > receivedAt
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Delivery date cannot be after the receive date',
          path: ['deliveryDate'],
        });
      }

      if (
        isDeliveryDateChanged &&
        data.deliveryDate &&
        data.deliveryDate > DateTime.now()
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Delivery date cannot be in the future',
          path: ['deliveryDate'],
        });
      }

      // Check if toLocation is set (not null)
      if (!data.deliveryLocation) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Delivery location is required',
          path: ['deliveryLocation'],
        });
      }
    });
}

type FormData = z.infer<ReturnType<typeof createDeliveryFormSchema>>;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  shipment?: Shipment;
}

export function DeliveryDialog(props: Props) {
  const queryClient = useQueryClient();
  const isEditMode = !!props.shipment?.deliveredAt;
  const isReceived = !!props.shipment?.receivedAt;
  const isDelivered = !!props.shipment?.deliveredAt;

  // Only initial delivery (not edit) triggers AEG cascade on the backend
  const serializedAssetCount = !isEditMode
    ? getTotalSerializedAssetCount(props.shipment?.items ?? [])
    : 0;
  const estimatedCascadeSeconds =
    calculateCascadeTimeSeconds(serializedAssetCount);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [inlineErrorMessage, setInlineErrorMessage] = useState<string | null>(
    null
  );
  const [countdownSeconds, setCountdownSeconds] = useState(0);

  const wasOpenRef = useRef(props.isOpen);

  const getDefaultValues = useCallback(() => {
    if (isEditMode && props.shipment) {
      return {
        deliveryDate: props.shipment.deliveredAt,
        deliveryNote: props.shipment.deliveryNote || null,
        deliveryLocation: props.shipment.toLocation,
      };
    }
    // For initial confirm delivery (not edit mode), default to current time with 0 seconds/milliseconds
    return {
      deliveryDate: DateTime.now().set({ second: 0, millisecond: 0 }),
      deliveryNote: null,
      deliveryLocation: props.shipment?.toLocation ?? null,
    };
  }, [isEditMode, props.shipment]);

  const { control, reset, handleSubmit, setError } = useForm<FormData>({
    resolver: zodResolver(
      createDeliveryFormSchema(
        props.shipment?.shippedAt ?? null,
        props.shipment?.receivedAt ?? null,
        props.shipment?.deliveredAt ?? null
      )
    ),
    defaultValues: getDefaultValues(),
  });

  // Reset the form only when the dialog opens (transitions from closed to
  // open) so edits made after mount are reflected. Resetting on every change
  // to props.shipment would clobber in-progress edits whenever the shipment is
  // refetched in the background (e.g. the 30s poll or a cascade job).
  useEffect(() => {
    const isOpening = props.isOpen && !wasOpenRef.current;
    wasOpenRef.current = props.isOpen;

    if (isOpening) {
      reset(getDefaultValues());
    }
  }, [props.isOpen, props.shipment, isEditMode, reset, getDefaultValues]);

  const handleClose = () => {
    setShowSnackbar(false);
    setInlineErrorMessage(null);
    props.onClose();
  };

  // Shared error handler: 2205 (date outside ship-to-receipt) maps to a
  // field-level error. Any other curated code renders inline.
  // Unmapped errors fire Sentry and surface in the SupportSnackbar.
  const handleMutationError = (error: AxiosError) => {
    if (isPlanLimitError(error)) {
      return;
    }

    const apiError = handleAPIError(error);
    if (apiError.apiStatusCode === ASSET_MOVE_CONFLICT_CODE) {
      setInlineErrorMessage(getMoveConflictMessage(apiError.conflictingAssets));
      setShowSnackbar(false);
      return;
    }
    if (apiError.apiStatusCode === 2205) {
      setError('deliveryDate', {
        type: 'manual',
        message:
          'Delivery date must be on or after the ship date and on or before the receive date',
      });
      setShowSnackbar(false);
      setInlineErrorMessage(null);
      return;
    }
    const curated = getUserFacingErrorMessage(apiError);
    if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
      setInlineErrorMessage(curated);
      setShowSnackbar(false);
      return;
    }
    if (!apiError.sentToSentry) {
      captureException(error);
    }
    setInlineErrorMessage(null);
    setShowSnackbar(true);
  };

  const deliverShipmentMutation = useMutation({
    mutationFn: (data: DeliverShipmentRequest) =>
      deliverShipment(props.shipment?.key ?? '', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shipmentsQueryKey });
      queryClient.invalidateQueries({
        queryKey: shipmentDetailQueryKey(props.shipment?.key ?? ''),
      });
      queryClient.invalidateQueries({ queryKey: fulfillmentsQueryKey });
      handleClose();
    },
    onError: handleMutationError,
  });

  const updateShipmentMutation = useMutation({
    mutationFn: (data: UpdateShipmentRequest) =>
      updateShipment(props.shipment?.key ?? '', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shipmentsQueryKey });
      queryClient.invalidateQueries({
        queryKey: shipmentDetailQueryKey(props.shipment?.key ?? ''),
      });
      queryClient.invalidateQueries({ queryKey: fulfillmentsQueryKey });
      handleClose();
    },
    onError: handleMutationError,
  });

  // Countdown timer — counts down from estimatedCascadeSeconds while confirming delivery
  useEffect(() => {
    if (!deliverShipmentMutation.isPending) {
      return;
    }
    setCountdownSeconds(estimatedCascadeSeconds);
    const interval = setInterval(() => {
      setCountdownSeconds((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [deliverShipmentMutation.isPending, estimatedCascadeSeconds]);

  const onSubmit = async (data: FormData) => {
    if (isEditMode) {
      const updateData: UpdateShipmentRequest = {
        deliveryNote: data.deliveryNote ?? null,
      };

      // The delivery time is sent only when the person picked a new one, so a stored time keeps its seconds.
      const storedDeliveredAt = props.shipment?.deliveredAt;
      if (!storedDeliveredAt || !data.deliveryDate!.equals(storedDeliveredAt)) {
        // Convert to UTC before serializing, with seconds and milliseconds zeroed
        const deliveryDate = data.deliveryDate!.set({
          second: 0,
          millisecond: 0,
        });
        updateData.deliveredAt = deliveryDate.toUTC().toISO();
        updateData.deliveredAtTimezone = deliveryDate.zoneName;
      }

      // Only include toLocationId if the shipment hasn't been delivered or received
      if (!isDelivered && !isReceived) {
        updateData.toLocationId = data.deliveryLocation?.id ?? null;
      }

      return updateShipmentMutation.mutate(updateData);
    }
    // Ensure seconds and milliseconds are 0
    const deliveryDate = data.deliveryDate!.set({ second: 0, millisecond: 0 });
    const timezone = deliveryDate.zoneName;
    const deliverData = {
      deliveryNote: data.deliveryNote ?? undefined,
      deliveryLocationId: data.deliveryLocation?.id ?? '',
      deliveredAt: deliveryDate.toUTC(),
      deliveredAtTimezone: timezone,
    };
    return deliverShipmentMutation.mutate(deliverData);
  };

  // Dialog isn't valid without a shipment
  if (!props.shipment) {
    return null;
  }

  const isConfirming = deliverShipmentMutation.isPending;
  const isUpdating = updateShipmentMutation.isPending;
  const isMutating = isConfirming || isUpdating;

  return (
    <Dialog
      key={props.shipment?.key} // Force remount when shipment changes
      open={props.isOpen}
      disableEscapeKeyDown={isMutating}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick' || isMutating) {
          return;
        }
        props.onClose();
      }}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(onSubmit),
      }}
    >
      <SupportSnackbar
        isOpen={showSnackbar}
        onClose={() => {
          setShowSnackbar(false);
          deliverShipmentMutation.reset();
          updateShipmentMutation.reset();
        }}
        message="An error occurred confirming delivery."
      />
      <DialogTitle>
        {isEditMode ? 'Edit delivery details' : 'Mark shipment as delivered'}
      </DialogTitle>
      <DialogContent sx={{ pb: 1.25 }}>
        <Stack gap={3}>
          {inlineErrorMessage && (
            <Alert severity="error" onClose={() => setInlineErrorMessage(null)}>
              {inlineErrorMessage}
            </Alert>
          )}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {`Record delivery details for shipment ${props.shipment.shipmentNumber}`}
            </Typography>
          </Box>
          <FormDateTimePicker
            disableFuture
            minimumDate={props.shipment?.shippedAt ?? undefined}
            maximumDate={props.shipment?.receivedAt ?? undefined}
            name="deliveryDate"
            label="Delivery date"
            control={control}
            isDisabled={false}
          />
          <ConditionalTooltip
            arrow
            tooltipText={
              isDelivered
                ? 'Location cannot be modified once shipment has been delivered'
                : isReceived
                  ? 'Location cannot be modified once units have been received'
                  : undefined
            }
            sx={{ width: '100%' }}
          >
            <Box sx={{ width: '100%' }}>
              <FormLocationSearch
                name={'deliveryLocation'}
                control={control}
                label="Actual delivery location"
                disabled={isReceived || isDelivered}
                onCreate={(name: string) => {
                  const url = getNewLocationURL(name);
                  window.open(url, '_blank');
                }}
              />
            </Box>
          </ConditionalTooltip>
          <FormTextField
            fullWidth
            isMultiline
            minimumRows={3}
            name="deliveryNote"
            label="Notes (optional)"
            placeholder={'Add any notes about this delivery'}
            control={control}
          />
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 2 }}
      >
        {/* Pre-submission warning — shown when there are serialized assets to update */}
        {!isConfirming && serializedAssetCount > 0 && (
          <Alert
            icon={<InfoOutlinedIcon fontSize="small" />}
            severity="info"
            sx={{
              mx: 2,
              py: 0.5,
              px: 1.5,
              alignItems: 'center',
              '& .MuiAlert-message': { py: 0 },
            }}
          >
            <Typography variant="body2" sx={{ lineHeight: 1 }}>
              {pluralize(serializedAssetCount, { singular: 'asset' })} will be
              delivered
            </Typography>
          </Alert>
        )}

        {/* In-flight info — shown on its own line when a slow update is in flight */}
        {isConfirming && serializedAssetCount > 0 && (
          <Alert
            icon={<InfoOutlinedIcon fontSize="small" />}
            severity="info"
            sx={{
              mx: 2,
              py: 0.5,
              px: 1.5,
              alignItems: 'center',
              '& .MuiAlert-message': { py: 0 },
            }}
          >
            <Typography variant="body2" sx={{ lineHeight: 1 }}>
              Updating {pluralize(serializedAssetCount, { singular: 'asset' })},
              please wait…
            </Typography>
          </Alert>
        )}

        {/* Estimated time + buttons */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          {estimatedCascadeSeconds > 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.5 }}
            >
              {isConfirming && countdownSeconds === 0
                ? 'Almost done...'
                : `Est. time: ${formatDuration(isConfirming ? countdownSeconds : estimatedCascadeSeconds, { short: true })}`}
            </Typography>
          )}
          <Box display="flex" gap={1}>
            <Button
              type="button"
              onClick={handleClose}
              disabled={isMutating}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={!props.shipment || isMutating}
              startIcon={
                isMutating ? <CircularProgress size={16} /> : undefined
              }
            >
              {isConfirming
                ? 'Confirming...'
                : isUpdating
                  ? 'Updating...'
                  : isEditMode
                    ? 'Update delivery'
                    : 'Confirm delivery'}
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
